import { type MbscCalendarColor } from '@mobiscroll/react';
import { addDays, eachDayOfInterval, isWeekend, min, startOfDay } from 'date-fns';

import { type GekaufterZeitraumTag, type Zeitraum } from '../../../dtos';
import { calendarColors } from '../../../theme/colorPalette';
import { isDateInZeitraum } from '../../../utils/zeitraum';
import { CalendarEvent, isSeminarraumEvent } from './helperClasses/CalendarEvent/CalendarEvent';
import { calendarEventStates } from './helperClasses/CalendarEvent/calendarEventStates';
import { CalendarExperteResource } from './helperClasses/CalendarResource/CalendarExperteResource';
import { CalendarGruppenraumResource } from './helperClasses/CalendarResource/CalendarGruppenraumResource';
import { CalendarSeminarraumResource } from './helperClasses/CalendarResource/CalendarSeminarraumResource';
import { CalendarStandortResource } from './helperClasses/CalendarResource/CalendarStandortResource';

export function getSlashedLinesColor(color1: string, color2: string): string {
  return `repeating-linear-gradient(135deg, ${color1}, ${color1} 5px, ${color2} 5px, ${color2} 10px)`;
}

export function getSelectedSeminarraumLocationKuerzel(events: CalendarEvent[]): string | undefined {
  const selectedRaumEvent = events.find((event) => event.isCurrentSelection && isSeminarraumEvent(event));

  if (selectedRaumEvent && isSeminarraumEvent(selectedRaumEvent)) {
    return selectedRaumEvent.locationKuerzel;
  } else {
    return undefined;
  }
}

type CalendarColor = MbscCalendarColor & Zeitraum;

const createSeminarraumColor = (
  raumResource: CalendarSeminarraumResource,
  availability: GekaufterZeitraumTag,
): CalendarColor => ({
  start: availability.start,
  end: availability.end,
  resource: raumResource.id,
  background: calendarColors.bookableResource,
});

const createGruppenraumColor = (gruppenraumResource: CalendarGruppenraumResource): MbscCalendarColor => ({
  recurring: { repeat: 'daily' },
  resource: gruppenraumResource.id,
  background: calendarColors.bookableResource,
});

const createStandortColor = (standortResource: CalendarStandortResource): MbscCalendarColor => ({
  recurring: { repeat: 'daily' },
  resource: standortResource.id,
  background: calendarColors.bookableResource,
});

const createExperteColors = (
  experteResource: CalendarExperteResource,
  timeRange: { start: Date; end: Date } | null,
): MbscCalendarColor[] => {
  const gueltigkeiten = experteResource.gueltigkeitenToEnforce();
  if (gueltigkeiten.length === 0) {
    return [
      {
        ...timeRange,
        resource: experteResource.id,
        background: experteResource.skipExperteGueltigkeitsCheck
          ? calendarColors.bookableResource
          : calendarColors.resource,
      },
    ];
  }

  if (!timeRange) {
    return [];
  }

  const timeRangeEndDate = timeRange.end;

  return gueltigkeiten.map((gueltigkeit) => ({
    start: startOfDay(gueltigkeit.start),
    // to avoid having mobiscroll calendar hanging up, we validate how far the gueltigkeit goes and cut it off if it goes too far into the future
    end: min([gueltigkeit.end, timeRangeEndDate]),
    resource: experteResource.id,
    background: calendarColors.bookableResource,
  }));
};

export const createAblaufColors = (date: Date, selection: CalendarEvent[]) => {
  const hasError = selection.some(
    (event) => event.state === calendarEventStates.ERROR && isDateInZeitraum(event, date),
  );
  if (hasError) {
    return {
      start: date,
      end: addDays(date, 1),
      background: getSlashedLinesColor(calendarColors.selectionArea, '#ffffff00'),
    };
  }

  return {
    start: date,
    end: addDays(date, 1),
    background: calendarColors.selectionArea,
  };
};

type CreateCalendarColorsParams = {
  seminarraumResources?: CalendarSeminarraumResource[];
  gruppenraumResources?: CalendarGruppenraumResource[];
  standortResources?: CalendarStandortResource[];
  experteResources?: CalendarExperteResource[];
  events?: CalendarEvent[];
  timeRange: { start: Date; end: Date } | null;
};

export const createCalendarColors = ({
  seminarraumResources = [],
  gruppenraumResources = [],
  standortResources = [],
  experteResources = [],
  events = [],
  timeRange,
}: CreateCalendarColorsParams): MbscCalendarColor[] => {
  const colors: MbscCalendarColor[] = [];
  const selectedSeminarraumLocationKuerzel = getSelectedSeminarraumLocationKuerzel(events);

  for (const resource of seminarraumResources) {
    for (const availability of resource.raum.gekaufterZeitraum) {
      colors.push(createSeminarraumColor(resource, availability));
    }
  }

  for (const resource of gruppenraumResources) {
    if (resource.location.kuerzel === selectedSeminarraumLocationKuerzel) {
      colors.push(createGruppenraumColor(resource));
    }
  }

  for (const resource of standortResources) {
    colors.push(createStandortColor(resource));
  }

  for (const resource of experteResources) {
    colors.push(...createExperteColors(resource, timeRange));
  }

  // get weekends from timeRange and add grey background color for them
  if (timeRange) {
    const weekendDates = eachDayOfInterval({
      start: timeRange.start,
      end: timeRange.end,
    }).filter((date) => isWeekend(date));

    for (const weekendDate of weekendDates) {
      colors.push({
        start: weekendDate,
        end: addDays(weekendDate, 1),
        background: calendarColors.weekend,
      });
    }
  }

  return colors;
};
